import styled from '@emotion/styled';
import * as packageIcon from '~/assets/icons/icon_package.svg';
import * as parcelIcon from '~/assets/icons/parcel.svg';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import * as React from 'react';
import { colors } from '~/styles/constants';
import CarrierParcelTemplate from '~/types/CarrierParcelTemplate';
import ParcelTemplate, {
  getDimensionsString,
  getName,
  getWeightString,
} from '~/types/ParcelTemplate';
import ListItem from './ListItem';
import SectionTitle from './SectionTitle';

const List = styled('ul')`
  margin: 0;
  padding: 0;
`;

const ZeroStateWrapper = styled('div')`
  display: flex;
  padding: 0 24px 24px 24px;
`;

const ZeroStateContainer = styled('div')`
  border-radius: 3px;
  background-color: #f7fafc;
  width: 100%;
  height: 140px;
  @media screen and (min-width: ${ResponsiveBreakPoints.SmallMobile}px) {
    height: 120px;
  }
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 24px;
`;

// TODO: use reusable Icon in circle (name tbd) component
const PackageIconContainer = styled('div')`
  height: 48px;
  width: 48px;
  min-width: 48px;
  min-height: 48px;
  background-color: ${colors.white};
  box-shadow: 0 2px 4px 0 rgba(214, 214, 214, 0.5);
  border-radius: 50%;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ZeroStateHelpText = styled('div')`
  text-align: center;
  padding: 10px 0;
`;

const CreatePackageLink = styled('div')`
  color: ${colors.primaryAlternative};
  cursor: pointer;
`;

export interface CustomParcelTemplateStateProps {
  parcelTemplates: readonly ParcelTemplate[];
  showZeroState?: boolean;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export interface CustomParcelTemplateDispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  openSavePackageTemplateModal: () => {};
}

type CustomParcelTemplateStoreProps = CustomParcelTemplateStateProps &
  CustomParcelTemplateDispatchProps;

interface CustomParcelTemplateProps extends CustomParcelTemplateStoreProps {
  onOptionSelection(parcel: Maybe<ParcelTemplate | CarrierParcelTemplate>): void;
  disableCustomDimensions?: boolean;
  invalid?: boolean;
  omitCustomParcelTemplate?: boolean;
  dataAnalytics?: string;
}

class CustomParcelTemplate extends React.Component<CustomParcelTemplateProps> {
  getParcelTemplateSubtitle = (parcelTemplate: ParcelTemplate) => {
    const dimensionsProvider =
      parcelTemplate.carrierTemplate && !parcelTemplate.carrierTemplate.isVariableDimensions
        ? parcelTemplate.carrierTemplate
        : parcelTemplate;

    const weightString = parcelTemplate.weight ? `, ${getWeightString(parcelTemplate)}` : '';

    return `${getDimensionsString(dimensionsProvider)}${weightString}`;
  };

  renderParcelTemplates = (parcelTemplate: ParcelTemplate) => {
    const { onOptionSelection, dataAnalytics } = this.props;

    return (
      <ListItem
        icon={parcelIcon}
        subtitle={this.getParcelTemplateSubtitle(parcelTemplate)}
        onClick={onOptionSelection.bind(this, parcelTemplate)}
        key={parcelTemplate.objectId}
        dataPendo="parcel-select-template"
        dataAnalytics={dataAnalytics}
      >
        {getName(parcelTemplate)}
      </ListItem>
    );
  };

  handleCreatePackageClick = () => {
    this.props.openSavePackageTemplateModal();
  };

  render() {
    const { parcelTemplates, showZeroState } = this.props;

    return (
      <React.Fragment>
        <SectionTitle onAddPackageClick={showZeroState ? this.handleCreatePackageClick : undefined}>
          Choose from package templates
        </SectionTitle>
        {parcelTemplates.length > 0 ? (
          <List>{parcelTemplates.map(this.renderParcelTemplates)}</List>
        ) : (
          showZeroState && (
            <ZeroStateWrapper>
              <ZeroStateContainer>
                <PackageIconContainer>
                  <img src={packageIcon} alt="" />
                </PackageIconContainer>
                <ZeroStateHelpText>
                  Package templates allow you to save your packaging measurements for easy re-use
                </ZeroStateHelpText>
                <CreatePackageLink onClick={this.handleCreatePackageClick}>
                  + Create Package Template
                </CreatePackageLink>
              </ZeroStateContainer>
            </ZeroStateWrapper>
          )
        )}
      </React.Fragment>
    );
  }
}

export default CustomParcelTemplate;
